/** Iter 639 — vendor panel split · shared constants + helpers
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React from "react";
import { sharedApi } from "@/lib/api";

export const KYC_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
// Iter-300 §1 — the streamed multipart endpoint accepts up to 8 MB per
// raw file (no base64 inflation), which fits multi-page bank statements
// without exceeding the ingress body cap.
export const MAX_KYC_BYTES = 8 * 1024 * 1024;
export const ENGAGEMENT_HEADERS = { "X-App-Platform": "nz_classified" };
// Iter-288e — query-param fallback for header-stripping proxies.
export const ENGAGEMENT_PARAM = { platform: "nz_classified" };

// Iter-316 §Bug2 — the backend requires BOTH docs before the application
// enters the admin review queue (emails only fire when both exist).
export const REQUIRED_KYC_DOCS = [
  { type: "owner_photo_id", label: "Owner photo ID" },
  { type: "business_registration_cert", label: "Business registration certificate" },
];

// Iter-316 addendum (a) — labels for the 8 server-computed checklist keys.
// `proof_of_business` = business registration certificate specifically
// (utility bill / lease no longer substitute — Iter 305).
export const KYC_CHECKLIST_LABELS = {
  legal_business_name: "Legal business name",
  physical_address: "Business address",
  business_phone: "Business phone",
  business_email: "Business email",
  owner_name: "Owner name",
  owner_photo_id: "Owner photo ID",
  proof_of_business: "Business registration certificate",
  bank_account_or_connect: "Bank account or Stripe payouts",
};

/** GET /vendors/me → vendor object, or null when the user isn't a vendor yet (404). */
export async function fetchVendorMe() {
  try {
    const { data } = await sharedApi.get(
      "/classifieds/engagement/vendors/me",
      { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
    );
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

// Iter-316 §Bug1 — derive the wizard step from the vendor record so a
// returning partner never loops on Step 1 (and never re-POSTs register).
export function stepFromVendor(vendor) {
  if (!vendor) return 1;
  if (vendor.kyc_status === "approved" || vendor.kyc_status === "rejected") return 4;
  const docsDone =
    !!vendor.kyc_checklist?.owner_photo_id && !!vendor.kyc_checklist?.proof_of_business;
  if (!docsDone) return 2;
  if (!vendor.stripe_account_id && !vendor.connect_ready) return 3;
  return 4;
}

export function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length > 0) {
    // Iter-288e (iteration_119) fix: surface Pydantic 422 field paths
    // so we know which fields the backend actually wants ("Field required
    // × 6" was useless — now shows "Missing: phone, physical_address …").
    const missing = d
      .filter((it) => /required|missing/i.test(it?.type || it?.msg || ""))
      .map((it) => {
        const path = Array.isArray(it.loc) ? it.loc.filter((p) => p !== "body").join(".") : null;
        return path || it.msg;
      })
      .filter(Boolean);
    if (missing.length) return `Missing fields: ${missing.join(", ")}`;
    if (d[0]?.msg) return d[0].msg;
  }
  if (err?.response) return fallback;
  return err?.message || fallback;
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
